import { RefObject, useCallback, useRef } from 'react';

/**
 * A collapsible section toggling `display: none` hides a list without
 * unmounting it, and a hidden element has no layout boxes — every measurement
 * reads 0. Returns a getter telling whether `ref` is currently box-less, so
 * callers can skip measuring until it is shown again.
 */
export const useIsElementHidden = (ref: RefObject<Element | null>) =>
  useCallback(() => (ref.current?.getClientRects().length ?? 0) === 0, [ref]);

/**
 * `measureElement` for `useVirtualizer` that ignores zero-height readings.
 *
 * While the list is hidden (see `useIsElementHidden`) the virtualizer's own
 * ResizeObserver still fires, reporting 0 for every item. Caching those zeroes
 * collapses the total size, so the range covers the whole list, every item
 * mounts and measures 0, and the observer fires again — a loop that locks the
 * main thread and leaves the list blank once shown. Reporting the item's last
 * non-zero height (or `estimateSize`) makes a zero measurement a no-op.
 */
export const useHiddenSafeMeasureElement = (estimateSize: number) => {
  const lastMeasuredSizeRef = useRef(new WeakMap<Element, number>());

  return useCallback(
    (el: Element, entry: ResizeObserverEntry | undefined) => {
      const box = entry?.borderBoxSize?.[0];
      const size = Math.round(
        box ? box.blockSize : el.getBoundingClientRect().height
      );
      if (size === 0) {
        return lastMeasuredSizeRef.current.get(el) ?? estimateSize;
      }
      lastMeasuredSizeRef.current.set(el, size);
      return size;
    },
    [estimateSize]
  );
};
